import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ExportOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import moment from 'moment';
import {
  Table, Input, Pagination, Button, message, Tag, Modal,
} from 'antd';
import qs from 'qs';
import {
  countUsers, listUsers, impersonateUser, archiveUser, deleteUser,
} from '../../services/Admin';
import { getDomainStatusEmoji } from '../../utils/utils';

function Users() {
  // Pagination
  const location = useLocation();
  const history = useHistory();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });
  const currentPage = +search.page || 1;
  const onPaginationChange = (page) => {
    const newSearch = {
      ...search,
      page,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Search
  const onSearchChange = (e) => {
    const newSearch = {
      ...search,
      q: e.target.value,
      page: 1,
    };
    history.push({
      ...location,
      search: qs.stringify(newSearch),
    });
  };
  // Data retrieving
  const { isLoading, data: users, refetch } = useQuery(
    ['listUsers', currentPage, search.q],
    () => listUsers({
      page: currentPage,
      q: search.q,
    }),
    { cacheTime: 0 },
  );
  const { data: count, isLoading: isCountLoading } = useQuery(
    ['countUsers', search.q],
    () => countUsers({ q: search.q }),
    { cacheTime: 0 },
  );

  // Actions
  const impersonate = async (user) => {
    try {
      const { sessionId } = await impersonateUser(user._id);
      window.open(`https://app.karmalinks.io/#/auth/${sessionId}`, '_blank').focus();
    } catch (error) {
      message.error('Unexpected error while performing action!');
    }
  };

  // Archive
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);

  const onArchive = async () => {
    setIsArchiving(true);
    try {
      await archiveUser(archiveTarget._id);
      message.success('User archived');
      setArchiveTarget(null);
      refetch();
    } catch (error) {
      const code = error.response?.data?.error?.code;
      if (code === 'ALREADY_ARCHIVED') {
        message.info('User is already archived');
        setArchiveTarget(null);
        refetch();
      } else {
        message.error('Unexpected error while performing action!');
      }
    } finally {
      setIsArchiving(false);
    }
  };

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteStep(1);
    setDeleteConfirmEmail('');
  };

  const onDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteUser(deleteTarget._id);
      message.success('User deleted');
      closeDeleteModal();
      refetch();
    } catch (error) {
      const code = error.response?.data?.error?.code;
      if (code === 'USER_NOT_FOUND') {
        message.error('User not found — they may have already been deleted');
      } else {
        message.error('Unexpected error while performing action!');
      }
      closeDeleteModal();
      refetch();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div id="Users">
      <div className="Filters">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search in email and name"
          defaultValue={search.q || ''}
          style={{ width: 260 }}
          onChange={debounce(onSearchChange, 500)}
          allowClear
        />
        <div style={{ flex: 1 }} />
        <Pagination
          current={currentPage}
          onChange={onPaginationChange}
          total={count}
          pageSize={20}
          showSizeChanger={false}
        />
      </div>
      <Table
        dataSource={users}
        loading={isLoading}
        pagination={false}
        rowKey="_id"
        size="small"
      >
        <Table.Column
          title=""
          key="_id"
          render={(_, u) => (u.emailVerified ? '✅' : '❌')}
        />
        <Table.Column
          title={`Email (${isCountLoading ? '...' : count})`}
          key="_id"
          dataIndex="email"
          render={(_, u) => (
            <>
              {u.email}
              {u.emailVerified && (
                <>
                  {' '}
                  <Button
                    type="link"
                    style={{
                      padding: 0,
                      height: 0,
                    }}
                    onClick={() => impersonate(u)}
                  >
                    <ExportOutlined />
                  </Button>
                </>
              )}
              {u.archivedAt && (
                <>
                  {' '}
                  <Tag color="orange">
                    {`Archived on ${moment(u.archivedAt).format('MMM D, YYYY')}`}
                  </Tag>
                </>
              )}
            </>
          )}
        />
        <Table.Column
          title="Li Name"
          key="_id"
          render={(_, u) => u.linkedinProfile?.name || '-'}
        />
        <Table.Column
          title="ref"
          key="_id"
          dataIndex="ref"
        />
        <Table.Column
          title="Domains"
          key="_id"
          render={(_, u) => (
            <div>
              {u.domains.map((d) => (
                <a key={d._id} href={`#/domains/?q=${d.name}`}>
                  <span style={{ fontSize: 10 }}>{getDomainStatusEmoji(d)}</span>
                  {' '}
                  {d.name}
                  {' '}
                </a>
              ))}
            </div>
          )}
        />
        <Table.Column
          title="Actions"
          key="actions"
          render={(_, u) => (
            <div style={{ whiteSpace: 'nowrap' }}>
              <Button
                size="small"
                disabled={!!u.archivedAt}
                style={!u.archivedAt ? { color: '#fa8c16', borderColor: '#fa8c16' } : {}}
                onClick={() => setArchiveTarget(u)}
              >
                {u.archivedAt ? 'Archived' : 'Archive'}
              </Button>
              {' '}
              <Button
                size="small"
                type="primary"
                danger
                onClick={() => setDeleteTarget(u)}
              >
                Delete
              </Button>
            </div>
          )}
        />
      </Table>

      {archiveTarget && (
        <Modal
          visible
          title="Archive User"
          footer={null}
          closable={false}
        >
          <p>
            Are you sure you want to archive
            {' '}
            <strong>{archiveTarget.email}</strong>
            ? This will cancel their subscription, pause all their
            domains, and lock them out of the app.
          </p>
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button disabled={isArchiving} onClick={() => setArchiveTarget(null)}>
              CANCEL
            </Button>
            {' '}
            <Button
              loading={isArchiving}
              style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }}
              onClick={onArchive}
            >
              ARCHIVE
            </Button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          visible
          title="Delete User"
          footer={null}
          closable={false}
        >
          {deleteStep === 1 && (
            <>
              <p>
                Are you sure you want to permanently delete
                {' '}
                <strong>{deleteTarget.email}</strong>
                ? This cannot be undone.
              </p>
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Button onClick={closeDeleteModal}>
                  CANCEL
                </Button>
                {' '}
                <Button type="primary" danger onClick={() => setDeleteStep(2)}>
                  NEXT
                </Button>
              </div>
            </>
          )}
          {deleteStep === 2 && (
            <>
              <p>
                Type
                {' '}
                <strong>{deleteTarget.email}</strong>
                {' '}
                to confirm deletion:
              </p>
              <Input
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                placeholder={deleteTarget.email}
              />
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Button disabled={isDeleting} onClick={closeDeleteModal}>
                  CANCEL
                </Button>
                {' '}
                <Button
                  type="primary"
                  danger
                  loading={isDeleting}
                  disabled={deleteConfirmEmail !== deleteTarget.email}
                  onClick={onDelete}
                >
                  DELETE
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

Users.propTypes = {
};

export default Users;
